import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

const Login = () => {
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const { login }   = useAuth();
  const navigate    = useNavigate();

  const emailRef    = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const emailVal    = (emailRef.current?.value    ?? '').trim().toLowerCase();
    const passwordVal = (passwordRef.current?.value ?? '');
    if (!emailVal || !passwordVal) {
      setError('Please enter your email and password.');
      setLoading(false);
      return;
    }
    try {
      const res = await api.post('/auth/login', { email: emailVal, password: passwordVal });
      login(res.data.token, res.data.user);
      navigate('/teams');
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (!err.response) {
        setError('Could not reach the server. Check your internet connection and try again.');
      } else {
        setError(`Login failed (${err.response.status}). Check your credentials.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-page)',
      padding: '20px',
    }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: '36px' }}>
        <div style={{
          width: 68, height: 68, borderRadius: '18px', margin: '0 auto 16px',
          background: 'linear-gradient(135deg, #F97316, #EA580C)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 12px 40px rgba(249,115,22,0.25)',
        }}>
          <svg width="34" height="34" viewBox="0 0 20 20" fill="none">
            <polygon points="6,3 17,10 6,17" fill="white"/>
            <rect x="3" y="3" width="2.5" height="14" rx="1.2" fill="white"/>
          </svg>
        </div>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 900,
          letterSpacing: '-0.5px', color: 'var(--ink-1)', marginBottom: '4px',
        }}>
          SLOW RIB
        </div>
        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--sr-amber)', letterSpacing: '2px', textTransform: 'uppercase' }}>
          Sports Streaming
        </div>
        <p style={{ color: 'var(--ink-3)', fontSize: '14px', marginTop: '10px' }}>
          Sign in to watch live games
        </p>
      </div>

      {/* Card */}
      <div style={{
        width: '100%', maxWidth: '400px',
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 16, padding: 32,
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
      }}>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email address</label>
            <input
              ref={emailRef}
              id="email" name="email" type="email" className="form-control"
              defaultValue=""
              placeholder="you@example.com" required
              autoComplete="email" inputMode="email"
              autoCapitalize="none" autoCorrect="off" spellCheck={false}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              ref={passwordRef}
              id="password" name="password" type="password" className="form-control"
              defaultValue=""
              placeholder="••••••••" required
              autoComplete="current-password"
            />
          </div>
          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 8, padding: '10px 14px', marginBottom: 16,
              fontSize: 13, color: 'var(--loss)', fontWeight: 500,
            }}>
              {error}
            </div>
          )}
          <button
            type="submit" className="btn btn-primary btn-lg"
            style={{ width: '100%', marginTop: 4, fontSize: 15 }}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>

      <p style={{ textAlign: 'center', marginTop: 24, color: 'var(--ink-3)', fontSize: 14 }}>
        Don't have an account?{' '}
        <Link to="/register" style={{ color: 'var(--sr-amber)', fontWeight: 700 }}>
          Create one free
        </Link>
      </p>
    </div>
  );
};

export default Login;
